import React from 'react';
import { useFragment } from 'react-relay/hooks';

import { Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { graphql } from 'babel-plugin-relay/macro';

import { ProvisioningEnum } from '@lib/resources/types';

import usePermissions from 'hooks/usePermissions';

import { Chat_Patient$key } from './__generated__/Chat_Patient.graphql';
import { Chat_Viewer$key } from './__generated__/Chat_Viewer.graphql';
import ActionsChatActivity, { ActionsChatActivityProps } from './ActionsChatActivity';
import ChatInput, { ChatInputProps } from './ChatInput';
import InfiniteScrollMessages from './InfiniteScrollMessages';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    infiniteScrollWrapper: {
      flex: '1 0 0',
      position: 'relative',
      overflowY: 'auto',
      padding: `${unit * 2.5}px`,
    },
    gap: {
      flex: 1,
    },
  });

export interface ChatProps extends WithStyles<typeof styles>, React.HTMLAttributes<HTMLDivElement> {
  fragmentRefViewer: Chat_Viewer$key;
  fragmentRef: Chat_Patient$key;
  variant: 'regular' | 'popper';
  source?: ChatInputProps['source'];
  includeActionsChatActivity?: boolean;
  onChatActivityAction?: ActionsChatActivityProps['onChatActivityAction'];
  shouldHideSenderSelector?: boolean;
}

const Chat: React.FC<ChatProps> = ({
  classes,
  fragmentRefViewer,
  fragmentRef,
  variant,
  source,
  includeActionsChatActivity,
  onChatActivityAction,
  shouldHideSenderSelector = false,
  ...rest
}) => {
  const viewer = useFragment<Chat_Viewer$key>(
    graphql`
      fragment Chat_Viewer on User {
        ...InfiniteScrollMessages_Viewer
      }
    `,
    fragmentRefViewer,
  );

  const patient = useFragment<Chat_Patient$key>(
    graphql`
      fragment Chat_Patient on Patient {
        ...InfiniteScrollMessages_Patient
        ...ChatInput_Patient
        chatActivity {
          status
          deleted
          ...ActionsChatActivity_ChatActivity
        }
      }
    `,
    fragmentRef,
  );

  const { hasAccessToProduct } = usePermissions();

  if (!patient) {
    return null;
  }
  return (
    <div {...rest}>
      <div className={classes.infiniteScrollWrapper}>
        <div className={classes.gap} />
        <InfiniteScrollMessages fragmentRefViewer={viewer} fragmentRef={patient} />
      </div>
      {hasAccessToProduct(ProvisioningEnum.Chat) &&
        (!includeActionsChatActivity || !patient.chatActivity || patient.chatActivity.status === 'assigned' ? (
          <ChatInput
            fragmentRef={patient}
            variant={variant === 'popper' ? 'dense' : 'regular'}
            source={source}
            shouldHideSenderSelector={shouldHideSenderSelector}
          />
        ) : (
          <ActionsChatActivity
            variant={variant}
            fragmentRef={patient.chatActivity}
            onChatActivityAction={onChatActivityAction}
          />
        ))}
    </div>
  );
};

export default withStyles(styles)(Chat);
